/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */
import {
  AmountJson,
  Amounts,
  CancellationToken,
  ConversionRate,
  HttpStatusCode,
  TalerConversionInfoConfig,
  TalerError,
  TalerErrorCode,
  assertUnreachable,
  encodeCrock,
  getRandomBytes,
} from "@gnu-taler/taler-util";
import {
  Attention,
  AsyncButton,
  Loading,
  RenderAmount,
  RouteDefinition,
  ShowInputErrorLabel,
  useBankCoreApiContext,
  useNotificationContext,
  useNotifiedOperation,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { Fragment, VNode, h } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

import {
  Paytos,
  TalerCorebankApi,
  opFixedSuccess,
} from "@gnu-taler/taler-util";
import { dummyHttpResponse } from "@gnu-taler/taler-util/http";
import { useBankChallengeHandlerContext } from "../../context/challenge.js";
import {
  revalidateAccountDetails,
  useAccountDetails,
} from "../../hooks/account.js";
import {
  TransCalc,
  revalidateConversionInfo,
  revalidateConversionRateForUser,
  useCashoutEstimatorByUser,
  useConversionInfo,
  useConversionRateForUser,
} from "../../hooks/regional.js";
import { LoggedIn, useSessionState } from "../../hooks/session.js";
import { TanChannel, undefinedIfEmpty } from "../../utils.js";
import { LoginForm } from "../LoginForm.js";
import { InputAmount, doAutoFocus } from "../PaytoWireTransferForm.js";
import { RetryableError } from "../../components/RetryableError.js";
import { OperationError } from "../../components/OperationError.js";

interface Props {
  account: string;
  focus?: boolean;
  onCashout: () => void;
  routeClose: RouteDefinition;
}

type FormType = {
  isDebit: boolean;
  amount: string;
  subject: string;
  channel: TanChannel;
};
type ErrorFrom<T> = {
  [P in keyof T]+?: string;
};

export function CreateCashout({
  account: accountName,
  onCashout,
  focus,
  routeClose,
}: Props): VNode {
  const { i18n } = useTranslationContext();
  const { config } = useBankCoreApiContext();
  const resultAccount = useAccountDetails(accountName);
  const { state: credentials } = useSessionState();
  const creds = credentials.status !== "loggedIn" ? undefined : credentials;
  const rateResp = useConversionRateForUser(accountName, creds?.token);
  const conversionResp = useConversionInfo();

  if (!config.allow_conversion) {
    return (
      <Fragment>
        <Attention type="warning" title={i18n.str`Unable to create a cashout`}>
          <i18n.Translate>
            The bank configuration does not support cashout operations.
          </i18n.Translate>
        </Attention>
        <div class="mt-5 sm:mt-6">
          <a
            href={routeClose.url({})}
            name="close"
            class="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <i18n.Translate>Close</i18n.Translate>
          </a>
        </div>
      </Fragment>
    );
  }

  if (!resultAccount) {
    return <Loading />;
  } else if (resultAccount instanceof TalerError) {
    return (
      <RetryableError
        error={resultAccount}
        title={i18n.str`Failed to load account details.`}
        onRetry={() => void revalidateAccountDetails()}
      />
    );
  } else if (resultAccount.type === "fail") {
    switch (resultAccount.case) {
      case HttpStatusCode.Unauthorized:
        return <LoginForm currentUser={accountName} />;
      case HttpStatusCode.NotFound:
        return <LoginForm currentUser={accountName} />;
      default:
        assertUnreachable(resultAccount);
    }
  }

  if (!conversionResp) {
    return <Loading />;
  } else if (conversionResp instanceof TalerError) {
    return (
      <RetryableError
        error={conversionResp}
        title={i18n.str`Failed to load conversion rate information.`}
        onRetry={() => void revalidateConversionInfo()}
      />
    );
  } else if (conversionResp.type === "fail") {
    switch (conversionResp.case) {
      case HttpStatusCode.NotImplemented: {
        return (
          <Attention type="danger" title={i18n.str`Cashout is disabled`}>
            <i18n.Translate>
              Cashout should be enabled in the configuration, the conversion
              rate should be initialized with fee(s), rates and a rounding mode.
            </i18n.Translate>
          </Attention>
        );
      }
      default:
        assertUnreachable(conversionResp);
    }
  }

  if (!rateResp) {
    return <Loading />;
  } else if (rateResp instanceof TalerError) {
    return (
      <RetryableError
        error={rateResp}
        title={i18n.str`Failed to load conversion rate information.`}
        onRetry={() => void revalidateConversionRateForUser()}
      />
    );
  } else if (rateResp.type === "fail") {
    switch (rateResp.case) {
      case HttpStatusCode.NotImplemented: {
        return (
          <Attention type="danger" title={i18n.str`Cashout is disabled`}>
            <i18n.Translate>
              Cashout should be enabled in the configuration, the conversion
              rate should be initialized with fee(s), rates and a rounding mode.
            </i18n.Translate>
          </Attention>
        );
      }
      default:
        assertUnreachable(rateResp);
    }
  }
  const rate = rateResp.body;
  if (!rate) {
    return (
      <Attention
        type="danger"
        title={i18n.str`The bank returned incomplete conversion information.`}
      >
        <i18n.Translate>Please contact the bank administrator.</i18n.Translate>
      </Attention>
    );
  }
  if (!creds) {
    return (
      <Attention type="warning" title={i18n.str`Authentication required`}>
        <i18n.Translate>Please sign in to create a cashout.</i18n.Translate>
      </Attention>
    );
  }

  return (
    <CreateCashoutInternal
      accountData={resultAccount.body}
      account={accountName}
      onCashout={onCashout}
      routeClose={routeClose}
      focus={focus}
      convConfig={conversionResp.body}
      rate={rateResp.body}
      session={creds}
    />
  );
}

function CreateCashoutInternal({
  onCashout,
  account: accountName,
  accountData,
  focus,
  routeClose,
  convConfig: {
    fiat_currency,
    fiat_currency_specification,
    regional_currency,
    regional_currency_specification,
  },
  session,
  rate,
}: Props & {
  convConfig: TalerConversionInfoConfig;
  rate: ConversionRate;
  session: LoggedIn;
  accountData: TalerCorebankApi.AccountData;
}): VNode {
  const {
    estimateByCredit: calculateFromCredit,
    estimateByDebit: calculateFromDebit,
  } = useCashoutEstimatorByUser(accountName);
  const [form, setForm] = useState<Partial<FormType>>({ isDebit: true });
  const [requestUid] = useState(() => encodeCrock(getRandomBytes(32)));
  const { showError } = useNotificationContext();
  const mfa = useBankChallengeHandlerContext();

  const { i18n } = useTranslationContext();
  const {
    lib: { bank: api },
  } = useBankCoreApiContext();

  const regionalZero = Amounts.zeroOfCurrency(regional_currency);
  const fiatZero = Amounts.zeroOfCurrency(fiat_currency!);

  const account = {
    balance: Amounts.parseOrThrow(accountData.balance.amount),
    balanceIsDebit: accountData.balance.credit_debit_indicator == "debit",
    debitThreshold: Amounts.parseOrThrow(accountData.debit_threshold),
  };

  const balanceLimit = IntAmounts.toIntAmount(
    account.balance,
    account.balanceIsDebit,
  ).increment(account.debitThreshold);

  const zeroCalc = {
    debit: regionalZero,
    credit: fiatZero,
    beforeFee: fiatZero,
  };
  const [calculationQuote, setCalculationQuote] = useState<{
    key: string;
    result: TransCalc;
  }>();
  const sellFee = Amounts.parseOrThrow(rate.cashout_fee);
  const sellRate = rate.cashout_ratio;
  /**
   * can be in regional currency or fiat currency
   * depending on the isDebit flag
   */
  const parsedInputAmount = Amounts.parse(
    `${form.isDebit ? regional_currency : fiat_currency}:${
      !form.amount ? "0" : form.amount
    }`,
  );
  const inputAmount =
    parsedInputAmount ?? (form.isDebit ? regionalZero : fiatZero);
  const calculationKey = `${form.isDebit === true ? "debit" : "credit"}:${
    form.amount?.trim() ?? ""
  }:${rate.cashout_fee}:${rate.cashout_ratio}`;

  async function calculateOperation(
    _ct: CancellationToken,
    _key: string,
    isDebit: boolean,
    input: AmountJson,
    fee: AmountJson,
  ) {
    const isNonZero = Amounts.isNonZero(input);
    const highEnough = isDebit
      ? Amounts.cmp(input, rate.cashout_min_amount) >= 0
      : true;
    if (isNonZero && highEnough) {
      return isDebit
        ? calculateFromDebit(input, fee)
        : calculateFromCredit(input, fee);
    } else {
      return opFixedSuccess(dummyHttpResponse, zeroCalc);
    }
  }

  // i18n.str`calculate conversion fee`,
  const conversionCalculator = useNotifiedOperation<
    Awaited<ReturnType<typeof calculateOperation>>,
    [string, boolean, AmountJson, AmountJson]
  >(calculateOperation, {
    onSuccess: (success, key) => setCalculationQuote({ key, result: success }),
    onFail: showError(
      i18n.str`Failed to calculate the conversion fee.`,
      (fail) => {
        switch (fail.case) {
          case HttpStatusCode.Conflict:
            return i18n.str`The amount is too small`;
          case HttpStatusCode.NotImplemented:
            return i18n.str`Conversion is not implemented.`;
          case TalerErrorCode.GENERIC_PARAMETER_MISSING:
            return i18n.str`At least a debit or credit amount must be provided.`;
          case TalerErrorCode.GENERIC_PARAMETER_MALFORMED:
            return i18n.str`The amount has an invalid format.`;
          case TalerErrorCode.GENERIC_CURRENCY_MISMATCH:
            return i18n.str`The currency is not supported`;
          default:
            assertUnreachable(fail);
        }
      },
    ),
  });
  const calculationRunning = conversionCalculator.running;
  const runCalculation = conversionCalculator.run;
  const lastCalculationAttempt = useRef<string>();

  useEffect(() => {
    if (
      calculationRunning ||
      lastCalculationAttempt.current === calculationKey
    ) {
      return;
    }
    lastCalculationAttempt.current = calculationKey;
    setCalculationQuote(undefined);
    void runCalculation(
      calculationKey,
      form.isDebit ?? false,
      inputAmount,
      sellFee,
    );
  }, [
    calculationKey,
    calculationRunning,
    form.isDebit,
    inputAmount,
    runCalculation,
    sellFee,
  ]);

  const calculationResult =
    calculationQuote?.key === calculationKey
      ? calculationQuote.result
      : undefined;
  const calc = calculationResult ?? zeroCalc;

  const balanceAfter = IntAmounts.toIntAmount(
    account.balance,
    account.balanceIsDebit,
  ).deduce(calc.debit).result;

  function updateForm(newForm: typeof form): void {
    if (form.amount !== newForm.amount || form.isDebit !== newForm.isDebit) {
      conversionCalculator.cancel();
      setCalculationQuote(undefined);
    }
    setForm(newForm);
  }
  const errors = undefinedIfEmpty<ErrorFrom<typeof form>>({
    subject: !form.subject ? i18n.str`Required` : undefined,
    amount: !form.amount
      ? i18n.str`Required`
      : !parsedInputAmount
        ? i18n.str`Invalid`
        : !calculationResult
          ? undefined
          : balanceLimit.result.negative ||
              Amounts.cmp(balanceLimit.result, calculationResult.debit) < 0
            ? i18n.str`The balance is not sufficient`
            : Amounts.cmp(calculationResult.debit, rate.cashout_min_amount) < 0
              ? i18n.str`It is not possible to cash out less than ${
                  Amounts.stringifyValueWithSpec(
                    Amounts.parseOrThrow(rate.cashout_min_amount),
                    regional_currency_specification,
                  ).normal
                }: ${Amounts.stringify(calculationResult.debit)}`
              : Amounts.isZero(calculationResult.credit)
                ? i18n.str`The total transfer to the destination will be zero`
                : undefined,
  });
  const trimmedAmountStr = form.amount?.trim();

  const subject = form.subject;

  // i18n.str`create cashout`,
  const cashout = useNotifiedOperation<
    Awaited<ReturnType<typeof api.createCashout>>,
    [LoggedIn, TransCalc, string, challengeIds?: string[]]
  >(
    (
      ct,
      session: LoggedIn,
      calc: TransCalc,
      subject: string,
      challengeIds?: string[],
    ) =>
      api.createCashout(
        session,
        {
          request_uid: requestUid,
          amount_credit: Amounts.stringify(calc.credit),
          amount_debit: Amounts.stringify(calc.debit),
          subject,
        },
        { challengeIds },
      ),
    {
      onSuccess: () => {
        mfa.cancel();
        // notifyInfo(i18n.str`Cashout created`);
        onCashout();
      },
      onFail: showError(
        i18n.str`Failed to create the cashout.`,
        (fail, session, calc, subject) => {
          switch (fail.case) {
            case HttpStatusCode.Accepted:
              mfa.onNewChallenge(
                // Translators: Dialog title for authenticating a cashout operation.
                i18n.str`Cashout`,
                session.username,
                fail.body,
                {
                  running: cashout.running,
                  cancel: cashout.cancel,
                  run: (challengeIds) =>
                    cashout.run(session, calc, subject, challengeIds),
                },
              );
              return undefined;
            case HttpStatusCode.NotFound:
              return i18n.str`Account not found`;
            case TalerErrorCode.BANK_TRANSFER_REQUEST_UID_REUSED:
              return i18n.str`Duplicate request detected, check if the operation succeeded or try again.`;
            case TalerErrorCode.BANK_BAD_CONVERSION:
              return i18n.str`The conversion rate was applied incorrectly`;
            case TalerErrorCode.BANK_UNALLOWED_DEBIT:
              return i18n.str`The account does not have sufficient funds`;
            case HttpStatusCode.NotImplemented:
              return i18n.str`Cashout is disabled`;
            case TalerErrorCode.BANK_CONFIRM_INCOMPLETE:
              return i18n.str`Missing cashout URI in the profile`;
            case TalerErrorCode.BANK_CONVERSION_AMOUNT_TO_SMALL:
              return i18n.str`The amount is below the minimum amount permitted.`;
            case TalerErrorCode.BANK_TAN_CHANNEL_SCRIPT_FAILED:
              return i18n.str`Sending the confirmation message failed, retry later or contact the administrator.`;
            case TalerErrorCode.BANK_TAN_CHANNEL_NOT_SUPPORTED: {
              return i18n.str`The server doesn't support the current TAN channel.`;
            }
            default:
              assertUnreachable(fail);
          }
        },
      ),
    },
  );

  const cashoutDisabled = !accountData.cashout_payto_uri;

  const cashoutAccount = !accountData.cashout_payto_uri
    ? undefined
    : Paytos.fromString(accountData.cashout_payto_uri);
  const cashoutAccountName =
    !cashoutAccount || cashoutAccount.tag === "error"
      ? undefined
      : cashoutAccount.value.displayName;

  const cashoutLegalName =
    !cashoutAccount || cashoutAccount.tag === "error"
      ? undefined
      : cashoutAccount.value.params["receiver-name"];

  return (
    <div>
      <div class="mt-6 overflow-hidden rounded-xl border border-onBackground/10 bg-white shadow-sm">
        <section class="border-b border-onBackground/10 bg-primary/5 px-5 py-5 sm:px-8">
          <h2
            id="summary-heading"
            class="text-lg font-semibold text-onBackground"
          >
            <i18n.Translate>Cashout details</i18n.Translate>
          </h2>
          <p class="mt-1 text-sm text-gray-600">
            <i18n.Translate>
              Review the destination account and conversion terms.
            </i18n.Translate>
          </p>

          <dl class="mt-5 grid gap-x-8 gap-y-3 sm:grid-cols-2">
            <div class="flex items-center justify-between gap-4">
              <dt class="text-sm text-gray-600">
                <i18n.Translate>Conversion rate</i18n.Translate>
              </dt>
              <dd class="text-sm text-onBackground">{sellRate}</dd>
            </div>

            <div class="flex items-center justify-between gap-4">
              <dt class="flex items-center text-sm text-gray-600">
                <span>
                  <i18n.Translate>Balance</i18n.Translate>
                </span>
              </dt>
              <dd class="text-sm text-onBackground">
                <RenderAmount
                  value={account.balance}
                  negative={account.balanceIsDebit}
                  withSign
                  spec={regional_currency_specification}
                />
              </dd>
            </div>
            <div class="flex items-center justify-between gap-4">
              <dt class="flex items-center text-sm text-gray-600">
                <span>
                  <i18n.Translate>Fee</i18n.Translate>
                </span>
              </dt>
              <dd class="text-sm text-onBackground">
                {Amounts.isZero(sellFee) ? (
                  <i18n.Translate>No fee</i18n.Translate>
                ) : (
                  <RenderAmount
                    value={sellFee}
                    negative
                    withSign
                    spec={fiat_currency_specification!}
                  />
                )}
              </dd>
            </div>
            {cashoutAccountName && cashoutLegalName ? (
              <Fragment>
                <div class="flex items-center justify-between gap-4">
                  <dt class="flex items-center text-sm text-gray-600">
                    <span>
                      <i18n.Translate>To account</i18n.Translate>
                    </span>
                  </dt>
                  <dd class="text-sm text-onBackground">
                    {cashoutAccountName}
                  </dd>
                </div>
                <div class="flex items-center justify-between gap-4">
                  <dt class="flex items-center text-sm text-gray-600">
                    <span>
                      <i18n.Translate>Legal name</i18n.Translate>
                    </span>
                  </dt>
                  <dd class="text-sm text-onBackground">{cashoutLegalName}</dd>
                </div>
                <p class="text-sm text-gray-500 sm:col-span-2">
                  <i18n.Translate>
                    If this name doesn't match the account holder's name, your
                    transaction may fail.
                  </i18n.Translate>
                </p>
              </Fragment>
            ) : (
              <div class="sm:col-span-2">
                <Attention type="warning" title={i18n.str`Unable to cash out`}>
                  <i18n.Translate>
                    Before being able to cash out to a bank account, you need to
                    complete your profile
                  </i18n.Translate>
                </Attention>
              </div>
            )}
          </dl>
        </section>
        <form
          class="bg-white"
          autoCapitalize="none"
          autoCorrect="off"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div class="px-5 py-6 sm:px-8">
            <div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              {/* subject */}

              <div class="sm:col-span-5">
                <label
                  class="block text-sm font-medium leading-6 text-onBackground"
                  for="subject"
                >
                  {i18n.str`Transfer reference`}
                  <b class="text-[red]"> *</b>
                </label>
                <div class="mt-2">
                  <input
                    ref={focus ? doAutoFocus : undefined}
                    type="text"
                    class="block w-full rounded-md disabled:bg-gray-200 border-0 py-1.5 text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 data-[error=true]:ring-red-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                    name="subject"
                    id="subject"
                    disabled={cashoutDisabled}
                    data-error={!!errors?.subject && form.subject !== undefined}
                    value={form.subject ?? ""}
                    placeholder={i18n.str`What is this cashout for?`}
                    onChange={(e) => {
                      form.subject = e.currentTarget.value;
                      updateForm(structuredClone(form));
                    }}
                    autocomplete="off"
                  />
                  <ShowInputErrorLabel
                    message={errors?.subject}
                    isDirty={form.subject !== undefined}
                  />
                </div>
              </div>

              <fieldset class="sm:col-span-5">
                <legend class="block text-sm font-medium leading-6 text-onBackground">
                  <i18n.Translate>
                    Enter the amount to send or receive
                  </i18n.Translate>
                </legend>

                <div class="mt-2 inline-flex rounded-lg bg-onBackground/5 p-1">
                  <button
                    type="button"
                    name="send regional currency"
                    aria-pressed={form.isDebit === true}
                    data-selected={form.isDebit === true}
                    disabled={cashoutDisabled}
                    class="rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:text-brand data-[selected=true]:bg-white data-[selected=true]:text-brand data-[selected=true]:shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={(e) => {
                      e.preventDefault();
                      form.isDebit = true;
                      updateForm(structuredClone(form));
                    }}
                  >
                    <i18n.Translate>Send {regional_currency}</i18n.Translate>
                  </button>
                  <button
                    type="button"
                    name="receive fiat currency"
                    aria-pressed={form.isDebit === false}
                    data-selected={form.isDebit === false}
                    disabled={cashoutDisabled}
                    class="rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:text-brand data-[selected=true]:bg-white data-[selected=true]:text-brand data-[selected=true]:shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={(e) => {
                      e.preventDefault();
                      form.isDebit = false;
                      updateForm(structuredClone(form));
                    }}
                  >
                    <i18n.Translate>Receive {fiat_currency}</i18n.Translate>
                  </button>
                </div>
              </fieldset>

              {/* amount */}
              <div class="sm:col-span-5">
                <div class="flex justify-between">
                  <label
                    class="block text-sm font-medium leading-6 text-onBackground"
                    for="amount"
                  >
                    {i18n.str`Amount`}
                    <b class="text-[red]"> *</b>
                  </label>
                </div>
                <div class="mt-2">
                  <InputAmount
                    name="amount"
                    left
                    currency={form.isDebit ? regional_currency : fiat_currency!}
                    value={trimmedAmountStr}
                    onChange={
                      cashoutDisabled
                        ? undefined
                        : (value) => {
                            form.amount = value;
                            updateForm(structuredClone(form));
                          }
                    }
                  />
                  <ShowInputErrorLabel
                    message={errors?.amount}
                    isDirty={form.amount !== undefined}
                  />
                </div>
              </div>

              {Amounts.isZero(calc.credit) ? undefined : (
                <div class="sm:col-span-5">
                  <dl class="mt-2 divide-y divide-onBackground/10 rounded-lg bg-background px-4 py-2 text-sm ring-1 ring-onBackground/10">
                    <div class="flex items-center justify-between py-2">
                      <dt class="text-sm text-gray-600">
                        <i18n.Translate>Total cost</i18n.Translate>
                      </dt>
                      <dd class="text-sm text-onBackground">
                        <RenderAmount
                          value={calc.debit}
                          negative
                          withColor
                          spec={regional_currency_specification}
                        />
                      </dd>
                    </div>

                    <div class="flex items-center justify-between py-2">
                      <dt class="flex items-center text-sm text-gray-600">
                        <span>
                          <i18n.Translate>Balance left</i18n.Translate>
                        </span>
                      </dt>
                      <dd class="text-sm text-onBackground">
                        <RenderAmount
                          value={balanceAfter}
                          negative={balanceAfter.negative}
                          withSign
                          spec={regional_currency_specification}
                        />
                      </dd>
                    </div>
                    {Amounts.isZero(sellFee) ||
                    Amounts.isZero(calc.beforeFee) ? undefined : (
                      <div class="flex items-center justify-between py-2">
                        <dt class="flex items-center text-sm text-gray-600">
                          <span>
                            <i18n.Translate>Before fee</i18n.Translate>
                          </span>
                        </dt>
                        <dd class="text-sm text-onBackground">
                          <RenderAmount
                            value={calc.beforeFee}
                            spec={fiat_currency_specification!}
                          />
                        </dd>
                      </div>
                    )}
                    <div class="flex items-center justify-between py-2">
                      <dt class="font-medium text-onBackground">
                        <i18n.Translate>Total cashout transfer</i18n.Translate>
                      </dt>
                      <dd class="font-semibold text-onBackground">
                        <RenderAmount
                          value={calc.credit}
                          withColor
                          spec={fiat_currency_specification!}
                        />
                      </dd>
                    </div>
                  </dl>
                </div>
              )}
            </div>
          </div>

          <div class="border-t border-onBackground/10 bg-background/60 px-5 py-4 sm:px-8">
            <OperationError class="mb-4" />
            <div class="flex items-center justify-between gap-x-6">
              <a
                href={routeClose.url({})}
                name="cancel"
                class="text-sm font-semibold leading-6 text-onBackground"
              >
                <i18n.Translate>Cancel</i18n.Translate>
              </a>
              <AsyncButton
                submit
                name="cashout"
                class="disabled:opacity-50 disabled:cursor-default cursor-pointer rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                disabled={
                  !!errors ||
                  !subject ||
                  !calculationResult ||
                  conversionCalculator.running
                }
                onClick={() => {
                  if (!calculationResult) return;
                  void cashout.run(session, calculationResult, subject!);
                }}
              >
                <i18n.Translate>Cash out</i18n.Translate>
              </AsyncButton>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
export interface IntAmountJson extends AmountJson {
  negative: boolean;
  saturated: boolean;
}

/**
 * Helper to manipulate amounts that can be negative.
 *
 */
export class IntAmounts {
  readonly result: IntAmountJson;
  private constructor(
    value: AmountJson,
    negative: boolean = false,
    saturated: boolean = false,
  ) {
    this.result = {
      ...value,
      negative,
      saturated,
    };
  }

  static from(value: IntAmountJson): IntAmounts {
    return new IntAmounts(value, value.negative, value.saturated);
  }

  static toIntAmount(value: AmountJson, negative: boolean = false): IntAmounts {
    return new IntAmounts(value, negative);
  }

  getResultZeroIfNegative(): IntAmountJson {
    return this.result.negative
      ? IntAmounts.toIntAmount(Amounts.zeroOfCurrency(this.result.currency))
          .result
      : this.result;
  }
  /**
   * Sum this value to a positive or negative amount.
   *
   * @param d
   * @returns
   */
  merge(d: IntAmountJson): IntAmounts {
    if (d.negative) {
      return this.deduce(d);
    } else {
      return this.increment(d);
    }
  }
  /**
   * Deduce the absolute value by the amount
   *
   * @param am
   * @returns
   */
  deduce(am: AmountJson): IntAmounts {
    if (this.result.negative) {
      const { amount, saturated } = Amounts.add(this.result, am);
      return IntAmounts.from({
        ...amount,
        saturated,
        negative: true,
      });
    } else {
      const negative = Amounts.cmp(this.result, am) < 0;
      const { amount, saturated } = negative
        ? Amounts.sub(am, this.result)
        : Amounts.sub(this.result, am);
      return IntAmounts.from({
        ...amount,
        negative,
        saturated,
      });
    }
  }
  /**
   * Increment the value by the amount
   *
   * @param am
   * @returns
   */
  increment(am: AmountJson): IntAmounts {
    if (this.result.negative) {
      const negative = Amounts.cmp(this.result, am) > 0;
      const { amount, saturated } = negative
        ? Amounts.sub(this.result, am)
        : Amounts.sub(am, this.result);
      return IntAmounts.from({
        ...amount,
        negative,
        saturated,
      });
    } else {
      const { amount, saturated } = Amounts.add(this.result, am);
      return IntAmounts.from({
        ...amount,
        saturated,
        negative: false,
      });
    }
  }
}
